'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { PricingForm } from './pricing-form';
import { PricingOTPForm } from './pricing-otp-form';
import { PricingCalculator } from './pricing-calculator';
import { countryDialCodes } from '@/lib/data/country-dial-codes';

type PricingItem = {
    translationKey: string;
    price: number;
    categoryKey: string;
};

type PricingData = {
    plans: PricingItem[];
    addOns: PricingItem[];
};

type Step = 'form' | 'otp' | 'verified';

export function PricingAccessSection() {
    const t = useTranslations('pricing');
    const [step, setStep] = useState<Step>('form');
    const [isPopupOpen, setIsPopupOpen] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        country: '',
    });
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pricingData, setPricingData] = useState<PricingData | null>(null);

    const fetchPricingData = useCallback(async () => {
        try {
            setError(null);
            const response = await fetch('/api/pricing/items', {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store',
            });
            if (!response.ok) {
                const result = await response.json().catch(() => ({}));
                if (response.status === 401) {
                    setStep('form');
                    setPricingData(null);
                    throw new Error(result?.error ?? t('messages.verificationRequired'));
                }
                throw new Error(result?.error ?? t('messages.unableToFetch'));
            }
            const result = (await response.json()) as { data?: PricingData };
            if (result?.data) {
                setPricingData(result.data);
            }
        } catch (err) {
            if (err instanceof Error) {
                setMessage(null);
                setError(err.message);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        async function checkVerification() {
            try {
                const response = await fetch('/api/pricing/status', {
                    signal: controller.signal,
                    credentials: 'include',
                });
                const result = (await response.json()) as { verified?: boolean };
                if (result?.verified) {
                    setStep('verified');
                    setIsPopupOpen(false);
                    await fetchPricingData();
                }
            } catch (err) {
                // ignore aborted or network errors for initial check
            }
        }
        checkVerification();
        return () => controller.abort();
    }, [fetchPricingData]);

    async function handleSubmitForm(data: { name: string; email: string; country: string; contact: string }) {
        setIsLoading(true);
        setError(null);
        setMessage(null);
        setFormData({ name: data.name, email: data.email, country: data.country });

        try {
            const response = await fetch('/api/pricing/request-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(data),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result?.error ?? t('messages.unableToSendOtp'));
            }

            // Show success message - OTP has been sent to email
            const message = result?.message ?? t('messages.otpSent');
            setMessage(message);
            setStep('otp');
        } catch (err) {
            setError(err instanceof Error ? err.message : t('messages.failedToRequestOtp'));
        } finally {
            setIsLoading(false);
        }
    }

    async function handleVerifyOtp(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!otp.trim() || isLoading) {
            return;
        }

        setIsLoading(true);
        setError(null);
        setMessage(null);

        try {
            const response = await fetch('/api/pricing/verify-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    email: formData.email.trim().toLowerCase(),
                    otp: otp.trim(),
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result?.error ?? t('messages.invalidOtp'));
            }

            setMessage(result?.message ?? t('messages.verificationSuccessful'));
            setStep('verified');
            setOtp('');
            setIsPopupOpen(false);
            await fetchPricingData();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('messages.verificationFailed'));
        } finally {
            setIsLoading(false);
        }
    }

    const handleExportPDF = async (selectedPlans: PricingItem[], selectedAddOns: PricingItem[], totalPrice: number) => {

        if (selectedPlans.length === 0 && selectedAddOns.length === 0) {
            const errorMsg = t('calculator.errors.selectToExport');
            setError(errorMsg);

            return;
        }

        setIsLoading(true);
        setError(null);
        setMessage(null);

        try {

            const pdfExport = await import('@/lib/pdf-export').catch((err) => {

                return null;
            });

            if (!pdfExport) {
                const errorMsg = t('calculator.errors.pdfNotAvailable');
                setError(errorMsg);

                return;
            }

            const result = await pdfExport.exportPricingToPDF(
                selectedPlans,
                selectedAddOns,
                totalPrice,
                formData
            );

            if (!result.success && result.error) {
                setError(result.error);

            } else if (result.success) {
                const successMsg = t('calculator.summary.exportPdf') + ' - Success!';
                setMessage(successMsg);

                setTimeout(() => setMessage(null), 5000);
            }
        } catch (error) {

            const errorMsg = t('calculator.errors.pdfFailed');
            setError(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Form Popup */}
            {isPopupOpen && step === 'form' && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            // Don't allow closing on background click for form step
                        }
                    }}
                >
                    <PricingForm onSubmit={handleSubmitForm} isLoading={isLoading} error={error} />
                </div>
            )}

            {/* OTP Popup */}
            {isPopupOpen && step === 'otp' && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            // Don't allow closing on background click for OTP step
                        }
                    }}
                >
                    <PricingOTPForm
                        email={formData.email}
                        otp={otp}
                        onOtpChange={setOtp}
                        onVerify={handleVerifyOtp}
                        onBack={() => {
                            setStep('form');
                            setError(null);
                            setMessage(null);
                        }}
                        isLoading={isLoading}
                        message={message}
                        error={error}
                    />
                </div>
            )}

            {/* Pricing Calculator */}
            {step === 'verified' && pricingData && (
                <PricingCalculator
                    pricingData={pricingData}
                    formData={formData}
                    onExportPDF={handleExportPDF}
                    error={error}
                    message={message}
                    onClearMessage={() => setMessage(null)}
                />
            )}
        </>
    );
}
